import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { PencilIcon, ArrowLeftIcon, TrashIcon } from '@heroicons/react/20/solid'
import { createCrudApi } from '../../lib/api'
import { nullToUndefined } from '../../lib/utils'
import { NoteForm } from '../../components/forms/NoteForm'
import { Avatar } from '../../components/ui/Avatar'
import { useTranslation } from '../../hooks/useTranslation'
import { useUserLookup } from '../../hooks/useUsers'
import { useFls } from '../../hooks/useFls'
import { RecordSharePanel } from '../../components/sharing/RecordSharePanel'
import { AuditHistoryPanel } from '../../components/audit/AuditHistoryPanel'
import type { Note, NoteInput, Account, Contact, Lead, Opportunity } from '../../../shared/types'

const notesApi = createCrudApi<Note, NoteInput>('notes')
const accountsApi = createCrudApi<Account, unknown>('accounts')
const contactsApi = createCrudApi<Contact, unknown>('contacts')
const leadsApi = createCrudApi<Lead, unknown>('leads')
const opportunitiesApi = createCrudApi<Opportunity, unknown>('opportunities')

function getRelatedEntityName(entity: Account | Contact | Lead | Opportunity | undefined, type: string): string {
  if (!entity) return '-'
  if (type === 'account') return (entity as Account).name
  if (type === 'contact') {
    const c = entity as Contact
    return [c.firstName, c.lastName].filter(Boolean).join(' ')
  }
  if (type === 'lead') {
    const l = entity as Lead
    return [l.firstName, l.lastName].filter(Boolean).join(' ')
  }
  if (type === 'opportunity') return (entity as Opportunity).name
  return '-'
}

export function NoteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const { t } = useTranslation()
  const { getUser } = useUserLookup()
  const { canRead, canWrite } = useFls('notes')

  const { data: note, isLoading } = useQuery({
    queryKey: ['notes', id],
    queryFn: () => notesApi.get(id!),
    enabled: !!id,
  })

  // Fetch the related entity based on type
  const { data: relatedEntity } = useQuery<Account | Contact | Lead | Opportunity | null>({
    queryKey: [note?.relatedToType, note?.relatedToId],
    queryFn: async () => {
      if (!note) return null
      switch (note.relatedToType) {
        case 'account': return accountsApi.get(note.relatedToId)
        case 'contact': return contactsApi.get(note.relatedToId)
        case 'lead': return leadsApi.get(note.relatedToId)
        case 'opportunity': return opportunitiesApi.get(note.relatedToId)
        default: return null
      }
    },
    enabled: !!note?.relatedToType && !!note?.relatedToId,
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<NoteInput>) => notesApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', id] })
      queryClient.invalidateQueries({ queryKey: ['notes'] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => notesApi.delete(id!),
    onSuccess: () => {
      navigate('/notes')
    },
  })

  if (isLoading) return <div>{t('common.loading')}</div>
  if (!note) return <div>{t('pages.notes.notFound')}</div>

  return (
    <>
      {/* Secondary column (desktop sidebar) */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-6">
          <Link
            to="/notes"
            className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {t('pages.notes.backToList')}
          </Link>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {note.title || t('pages.notes.noTitle')}
          </h1>
          <p className="mt-1 text-sm text-gray-500">{t('nav.notes')}</p>
        </div>

        <div className="space-y-3 mb-6">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
          >
            <PencilIcon className="h-4 w-4" />
            {isEditing ? t('common.cancel') : t('common.edit')}
          </button>
          <button
            onClick={() => {
              if (confirm(t('pages.notes.confirmDelete'))) {
                deleteMutation.mutate()
              }
            }}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
          >
            <TrashIcon className="h-4 w-4" />
            {t('common.delete')}
          </button>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">{t('common.quickInfo')}</h2>
          <dl className="space-y-4">
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.relatedTo')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {relatedEntity ? (
                  <Link
                    to={`/${note.relatedToType}s/${note.relatedToId}`}
                    className="text-brand-600 hover:text-brand-500"
                  >
                    {getRelatedEntityName(relatedEntity, note.relatedToType)}
                  </Link>
                ) : '-'}
                <span className="ml-2 text-xs text-gray-500 capitalize">({note.relatedToType})</span>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.created')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(note.createdAt).toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.owner')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {(() => {
                  const owner = getUser(note.ownerId)
                  if (!owner) return '-'
                  return (
                    <div className="flex items-center gap-x-2">
                      <Avatar firstName={owner.firstName} lastName={owner.lastName} size="xs" />
                      <span>{owner.firstName} {owner.lastName}</span>
                    </div>
                  )
                })()}
              </dd>
            </div>
          </dl>
        </div>

        {/* Record Sharing */}
        <RecordSharePanel entity="notes" entityId={id!} ownerId={note.ownerId ?? undefined} />

        {/* Audit History */}
        <AuditHistoryPanel entity="notes" entityId={id!} />
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] p-6">
        {/* Mobile header */}
        <div className="xl:hidden mb-6">
          <div className="mb-4">
            <Link
              to="/notes"
              className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              {t('pages.notes.backToList')}
            </Link>
          </div>
          <div className="flex gap-x-3">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="inline-flex items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
            >
              <PencilIcon className="h-4 w-4" />
              {isEditing ? t('common.cancel') : t('common.edit')}
            </button>
            <button
              onClick={() => {
                if (confirm(t('pages.notes.confirmDelete'))) {
                  deleteMutation.mutate()
                }
              }}
              className="inline-flex items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
            >
              <TrashIcon className="h-4 w-4" />
              {t('common.delete')}
            </button>
          </div>
        </div>

        {/* Detail card */}
        <div className="overflow-hidden bg-white shadow sm:rounded-lg">
          <div className="px-4 py-6 sm:px-6">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                {note.title || t('pages.notes.noTitle')}
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">{t('pages.notes.detailSubtitle')}</p>
            </div>
          </div>

          {isEditing ? (
            <div className="border-t border-gray-100 px-4 py-6 sm:px-6">
              <NoteForm
                defaultValues={nullToUndefined(note) as Partial<NoteInput>}
                onSubmit={(data) => updateMutation.mutate(data)}
                onCancel={() => setIsEditing(false)}
                loading={updateMutation.isPending}
                relatedEntityName={relatedEntity ? getRelatedEntityName(relatedEntity, note.relatedToType) : undefined}
                canWriteField={canWrite}
              />
            </div>
          ) : (
            <div className="border-t border-gray-100">
              <dl className="divide-y divide-gray-100">
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.title')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {note.title || t('pages.notes.noTitle')}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.body')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0 whitespace-pre-wrap">
                    {note.body}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.relatedTo')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {relatedEntity ? (
                      <Link
                        to={`/${note.relatedToType}s/${note.relatedToId}`}
                        className="text-brand-600 hover:text-brand-500"
                      >
                        {getRelatedEntityName(relatedEntity, note.relatedToType)}
                      </Link>
                    ) : '-'}
                    <span className="ml-2 text-xs text-gray-500 capitalize">({note.relatedToType})</span>
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.created')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {new Date(note.createdAt).toLocaleString()}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>
        </div>
      </div>
    </>
  )
}
